import api from '../api/axios';

// Translation service using backend API
class TranslationService {
  async translateText(text, targetLanguage, sourceLanguage = 'en') {
    if (!text || !text.trim()) return '';
    
    try {
      const response = await api.post('/i18n/translate', {
        text: text.trim(),
        targetLanguages: [targetLanguage],
        sourceLanguage,
      });

      if (response.data?.success && response.data?.data?.translations) {
        return response.data.data.translations[targetLanguage] || '';
      }
      
      return '';
    } catch (error) {
      console.error('Translation error:', error);
      // Return empty string if translation fails
      return '';
    }
  }

  // Translate text to multiple languages
  async translateToMultipleLanguages(text, targetLanguages, sourceLanguage = 'en') {
    if (!text || !text.trim()) {
      // Return empty object with all target languages
      const result = {};
      targetLanguages.forEach(lang => {
        result[lang] = '';
      });
      return result;
    }

    try {
      const response = await api.post('/i18n/translate', {
        text: text.trim(),
        targetLanguages,
        sourceLanguage,
      }, {
        timeout: 180000, // 3 minutes pentru traduceri mari (HTML content)
      });

      if (response.data?.success && response.data?.data?.translations) {
        return response.data.data.translations;
      }

      // Return empty translations on error
      const result = {};
      targetLanguages.forEach(lang => {
        result[lang] = '';
      });
      return result;
    } catch (error) {
      console.error('Translation error:', error);
      // Return empty translations on error
      const result = {};
      targetLanguages.forEach(lang => {
        result[lang] = '';
      });
      return result;
    }
  }

  // Translate sequentially with progress callback for large content
  async translateSequentially(text, targetLanguages, sourceLanguage = 'en', onProgress) {
    if (!text || !text.trim()) {
      const result = {};
      targetLanguages.forEach(lang => {
        result[lang] = '';
      });
      return result;
    }

    const result = {};
    
    for (let i = 0; i < targetLanguages.length; i++) {
      const targetLang = targetLanguages[i];
      
      try {
        if (onProgress) {
          onProgress(i + 1, targetLanguages.length, targetLang);
        }

        const response = await api.post('/i18n/translate', {
          text: text.trim(),
          targetLanguages: [targetLang],
          sourceLanguage,
        }, {
          timeout: 90000, // 90 seconds per language
        });

        if (response.data?.success && response.data?.data?.translations) {
          result[targetLang] = response.data.data.translations[targetLang] || '';
        } else {
          result[targetLang] = '';
        }
      } catch (error) {
        console.error(`Translation error for ${targetLang}:`, error);
        result[targetLang] = '';
      }
    }

    return result;
  }

  // Auto-translate all fields when default language changes
  async autoTranslateFields(defaultText, currentTranslations, languages, defaultLanguage) {
    if (!defaultText || !defaultText.trim()) {
      return currentTranslations;
    }

    const result = { ...currentTranslations };
    const targetLanguages = languages
      .filter(lang => lang.code !== defaultLanguage)
      .map(lang => lang.code);

    if (targetLanguages.length === 0) {
      return result;
    }

    try {
      const translations = await this.translateToMultipleLanguages(
        defaultText,
        targetLanguages,
        defaultLanguage
      );

      // Update only empty fields
      targetLanguages.forEach(langCode => {
        if (!result[langCode] || result[langCode].trim() === '') {
          result[langCode] = translations[langCode] || '';
        }
      });

      return result;
    } catch (error) {
      console.error('Auto-translate fields error:', error);
      return currentTranslations;
    }
  }
}

// Create singleton instance
const translationService = new TranslationService();
export default translationService;

